import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLFormElement} field - The discount field that morphs between button and input.
 * @property {HTMLButtonElement} toggle - The collapsed tertiary button.
 * @property {HTMLInputElement} input - The discount code input.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Cached collapsed (button) width in px, used to animate the collapse. */
  #collapsedWidth = 0;

  /** @type {Animation | null} */
  #widthAnimation = null;

  /**
   * The cart-drawer summary that gets lifted above the soft keyboard while the
   * input is focused (null on the cart page or when the summary isn't pinned).
   * @type {HTMLElement | null}
   */
  #keyboardLiftTarget = null;

  /**
   * Element the "remove discount" clicks are delegated on. The remove controls
   * live on the subtotal savings line, which sits outside this component but in
   * the same section, so we listen at the section root (falling back to self).
   * @type {HTMLElement | null}
   */
  #removeScope = null;

  /**
   * Whether the field is expanded. Derived from the DOM so it stays correct
   * after a section morph (a successful apply re-renders the collapsed markup).
   * @returns {boolean}
   */
  get #isExpanded() {
    return this.refs.field?.classList.contains('is-expanded') ?? false;
  }

  connectedCallback() {
    super.connectedCallback();
    // Collapse when focus leaves the component entirely (click/tab away). The
    // apply button uses mousedown preventDefault so clicking it never blurs the
    // input, so this only fires for genuine "click away" interactions.
    this.addEventListener('focusout', this.#handleFocusOut);

    this.#removeScope =
      (this.dataset.sectionId && document.getElementById(`shopify-section-${this.dataset.sectionId}`)) || this;
    this.#removeScope.addEventListener('click', this.#handleRemoveClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.#removeScope?.removeEventListener('click', this.#handleRemoveClick);
    this.#stopKeyboardTracking();
  }

  /**
   * Expands the button into the input field and focuses it.
   */
  expand = () => {
    if (this.#isExpanded) return;
    const { field, input, toggle } = this.refs;
    if (!field || !input) return;

    const start = field.getBoundingClientRect().width;
    this.#collapsedWidth = start;

    field.classList.add('is-expanded');
    toggle?.setAttribute('aria-expanded', 'true');
    if (toggle) toggle.tabIndex = -1;
    input.tabIndex = 0;

    const end = field.getBoundingClientRect().width;
    this.#animateWidth(start, end);

    input.focus({ preventScroll: true });
    this.#startKeyboardTracking();
  };

  /**
   * Collapses the input field back into the button.
   */
  #collapse() {
    if (!this.#isExpanded) return;
    const { field, input, toggle, cartDiscountError } = this.refs;
    if (!field || !input) return;

    const start = field.getBoundingClientRect().width;

    field.classList.remove('is-expanded');
    toggle?.setAttribute('aria-expanded', 'false');
    if (toggle) toggle.tabIndex = 0;
    input.tabIndex = -1;
    input.value = '';
    cartDiscountError?.classList.add('hidden');

    this.#stopKeyboardTracking();
    this.#animateWidth(start, this.#collapsedWidth || field.getBoundingClientRect().width);
  }

  /**
   * In the cart drawer the summary is pinned to the bottom of the viewport, so
   * the soft keyboard covers the discount input when it's focused. Track the
   * visual viewport and lift the pinned summary by the keyboard's height so the
   * field stays visible. Only runs inside the drawer when the summary is sticky;
   * on the cart page (or the non-sticky drawer layout) the page scrolls the
   * input into view natively.
   */
  #startKeyboardTracking() {
    if (!window.visualViewport) return;

    const summary = this.closest('.cart-drawer__summary');
    const dialog = this.closest('.cart-drawer__dialog');
    if (!(summary instanceof HTMLElement) || dialog?.getAttribute('cart-summary-sticky') === 'false') return;

    this.#keyboardLiftTarget = summary;
    window.visualViewport.addEventListener('resize', this.#updateKeyboardOffset);
    window.visualViewport.addEventListener('scroll', this.#updateKeyboardOffset);
    this.#updateKeyboardOffset();
  }

  #stopKeyboardTracking() {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.#updateKeyboardOffset);
      window.visualViewport.removeEventListener('scroll', this.#updateKeyboardOffset);
    }
    this.#keyboardLiftTarget?.style.removeProperty('--cart-keyboard-offset');
    this.#keyboardLiftTarget = null;
  }

  #updateKeyboardOffset = () => {
    const viewport = window.visualViewport;
    if (!viewport || !this.#keyboardLiftTarget) return;
    // How much the keyboard overlaps the bottom of the layout viewport.
    const overlap = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    this.#keyboardLiftTarget.style.setProperty('--cart-keyboard-offset', `${overlap}px`);
  };

  /**
   * Animates the field width between the button and input sizes.
   * @param {number} from
   * @param {number} to
   */
  #animateWidth(from, to) {
    const { field } = this.refs;
    if (!field || typeof field.animate !== 'function' || from === to) return;

    this.#widthAnimation?.cancel();
    this.#widthAnimation = field.animate(
      [{ width: `${from}px` }, { width: `${to}px` }],
      { duration: 200, easing: 'ease-in-out' }
    );
  }

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (!this.#isExpanded) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#collapse();
  };

  /**
   * Collapses on Escape from the input.
   * @param {KeyboardEvent} event
   */
  collapseOnEscape = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.#collapse();
    this.refs.toggle?.focus();
  };

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        // Codes are user-entered content — never in payloads (privacy rule).
        window.publishStorefrontEvent?.('discount_rejected', { reason: 'not_applicable' });
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (section) {
        const newComponent = section.querySelector('cart-discount-component');
        const codes = CartDiscount.#parseCodes(newComponent?.getAttribute('data-discount-codes') ?? undefined);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          window.publishStorefrontEvent?.('discount_rejected', { reason: 'shipping_not_applicable' });
          return;
        }
      }

      window.publishStorefrontEvent?.('discount_applied', {});
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Delegated click handler for the remove (×) controls on the applied
   * discount-code rows of the subtotal savings line.
   * @param {MouseEvent} event
   */
  #handleRemoveClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-discount-code]') : null;
    if (!target) return;
    const code = target.getAttribute('data-discount-code');
    if (code) this.#removeCode(code);
  };

  /**
   * Removes a single discount code from the cart and re-renders the section.
   * @param {string} discountCode
   */
  async #removeCode(discountCode) {
    if (typeof this.dataset.sectionId !== 'string') return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      window.publishStorefrontEvent?.('discount_removed', {});
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns the discount codes currently applied to the cart, read from the
   * component's data attribute (seeded in Liquid from the cart's applied codes).
   * @returns {string[]}
   */
  #existingDiscounts() {
    return CartDiscount.#parseCodes(this.dataset.discountCodes);
  }

  /**
   * @param {string | undefined} value - JSON array of codes.
   * @returns {string[]}
   */
  static #parseCodes(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter((code) => typeof code === 'string') : [];
    } catch {
      return [];
    }
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
